import React, { ReactNode, useContext, useEffect, useMemo, useRef } from 'react'

import { Carousel } from '@hhgtech/hhg-components/mantine'
import { Box, BoxProps } from '@mantine/core'
import Autoplay from 'embla-carousel-autoplay'
import filteredChildren from 'react-keyed-flatten-children'

import { CarouselProgressBar } from 'components/atoms/carouselProgressBar'
import { useIsMobile } from 'hooks/useIsMobile'

import { Arrows } from './arrows'
import { CarouselWrapper } from './carouselWrapper'
import { CarouselOrListingBlockContext } from './context'
import useStyles from './index.styles'

export type CarouselOrListingBlockSliderProps = {
  arrow?: boolean
} & BoxProps

export const Slider = ({
  children = [],
  arrow = true,
  styles,
  classNames,
  ...boxProps
}: CarouselOrListingBlockSliderProps) => {
  const {
    lengthToCarouselDesktop = 3,
    lengthToCarouselMobile = 2,
    percentOfEndSlideDesktop = 38,
    percentOfEndSlideMobile = 75,
    gapDesktop = 16,
    gapMobile = 16,
    showFullSlide,
    carouselSetting,
    disableCarousel,
    lazyLoad,
    onSlideChange,
    filteredChild,
    setFilteredChild,
    embla,
    setEmbla,
    autoPlay,
    delayTime,
    hideProgressBar,
  } = useContext(CarouselOrListingBlockContext)
  const isMobile = useIsMobile()
  const withIndicators = useMemo(
    () =>
      typeof carouselSetting?.withIndicators !== 'undefined'
        ? carouselSetting?.withIndicators
        : !lazyLoad && !isMobile,
    [isMobile, lazyLoad],
  )
  const { classes } = useStyles(
    {
      showFullSlide,
      gapMobile,
      gapDesktop,
      withIndicators,
    },
    {
      name: 'CarouselOrListingBlock__Slider',
      styles,
      classNames,
    },
  )
  const autoPlayRef = useRef(
    Autoplay({
      playOnInit: autoPlay,
      delay: delayTime,
    }),
  )

  const hasCarousel =
    filteredChild.length >=
    (isMobile ? lengthToCarouselMobile : lengthToCarouselDesktop)

  useEffect(() => {
    setFilteredChild(
      filteredChildren(children).filter((i) => React.isValidElement(i)),
    )
  }, [children])

  return (
    <Box {...boxProps}>
      {!disableCarousel && hasCarousel ? (
        <CarouselWrapper>
          <Carousel
            slideSize={
              showFullSlide
                ? isMobile
                  ? `${100 / (lengthToCarouselMobile - 1)}%`
                  : `${100 / (lengthToCarouselDesktop - 1)}%`
                : isMobile
                ? `${percentOfEndSlideMobile}%`
                : `${percentOfEndSlideDesktop}%`
            }
            loop={Boolean(showFullSlide)}
            align="start"
            slideGap={isMobile ? `${gapMobile}px` : `${gapDesktop}px`}
            getEmblaApi={setEmbla}
            withIndicators={withIndicators}
            withControls={false}
            plugins={[...(autoPlay && delayTime ? [autoPlayRef.current] : [])]}
            onSlideChange={onSlideChange}
            classNames={{
              root: classes.carouselRoot,
              viewport: classes.carouselViewport,
              indicators: classes.carouselIndicators,
              indicator: classes.carouselIndicator,
            }}
            {...carouselSetting}
            onMouseLeave={() =>
              autoPlay && delayTime && autoPlayRef?.current?.play()
            }
            onTouchEnd={() =>
              autoPlay && delayTime && autoPlayRef?.current?.play()
            }
          >
            {filteredChild.map((element: ReactNode, idx) => (
              <Carousel.Slide
                key={String(idx)}
                className={classes.sliderWrapper}
              >
                {element}
              </Carousel.Slide>
            ))}
          </Carousel>
          {isMobile && !hideProgressBar && !withIndicators ? (
            <CarouselProgressBar
              embla={embla}
              classNames={{
                bar: classes.progressBar,
                root: classes.progressRoot,
              }}
            />
          ) : null}
          {(!isMobile || arrow) && (
            <Arrows
              classNames={{
                arrow: classes.arrow,
                arrowPrev: classes.arrowPrev,
                arrowNext: classes.arrowNext,
                arrowIcon: classes.arrowIcon,
              }}
            />
          )}
        </CarouselWrapper>
      ) : (
        <Box
          className={classes.listing}
          data-slide-length={filteredChild.length}
        >
          {filteredChild.map((element: ReactNode, idx) => (
            <Box className={classes.itemWrapper} key={String(idx)}>
              {element}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}
